import { computeAdultsAndChildren } from '../helpers';
import { isPromotionalOffer, isPublicOffer } from '../helpers/isOffer.helper';
import type { Option, Room } from '../interfaces';
import { useBookingStore } from '../store';

import type {
  AvailableRoomsQuery,
  RoomEnhancementOption,
} from '~~/domains/graphql';
import {
  useAvailableRoomEnhancementOptionsQuery,
  useAvailableRoomsLazyQuery,
  useAvailableRoomsQuery,
} from '~~/domains/graphql';
import { useUserStore } from '~~/domains/user';

export type BookingRoomOptionEnhancement = {
  bookingOption: Option;
  apiOption: RoomEnhancementOption;
};

export function useBookingRoomApiData(
  bookingRoom: Ref<Room>,
  forceMemberOffer: boolean | Ref<boolean> = false,
) {
  const route = useRoute();
  const bookingStore = useBookingStore();
  const userStore = useUserStore();

  const identificationCookies = useHasIdentificationCookies();

  const availableRoomsData = ref<AvailableRoomsQuery | undefined>();

  const { adults, childrenAges } = computeAdultsAndChildren(
    bookingRoom?.value?.adults,
    bookingRoom?.value?.childrenAges || [],
    bookingStore.maxChildrenAge,
  );

  const hotelId = computed(
    () =>
      bookingStore.hotelId ||
      (route.params.ridcode as string) ||
      route.query.ridcode ||
      '0',
  );

  // Available room
  const availableRoomQueryVariables = computed<any>(() => ({
    adults,
    childrenAges,
    dateIn: bookingStore.dates.dateIn as string,
    hotelId: hotelId.value,
    nights: bookingStore.dates.nights,
  }));

  const { result } = useAvailableRoomsQuery(availableRoomQueryVariables.value, {
    errorPolicy: 'ignore',
  });

  watch(identificationCookies, async () => {
    if (identificationCookies.value) {
      const { load, onResult } = await useAvailableRoomsLazyQuery(
        availableRoomQueryVariables.value,
      );

      load();

      onResult(response => {
        if (response.loading) return;

        availableRoomsData.value = response.data;
      });
    }
  });

  const data = computed(() => {
    return result.value || availableRoomsData.value;
  });

  const availableRoom = computed(() =>
    data.value?.availableRooms?.find?.(
      available =>
        available?.availabilityRoom?.code === bookingRoom?.value?.productCode,
    ),
  );

  const accommodation = computed(() => availableRoom.value?.accommodationRoom);
  const availability = computed(() => availableRoom.value?.availabilityRoom);

  const offer = computed(() => {
    const memberEnforced = isRef(forceMemberOffer)
      ? forceMemberOffer.value
      : forceMemberOffer;

    const publicOffer = availableRoom.value?.availabilityRoom?.offers?.find(
      offer =>
        offer?.categories?.some(isPublicOffer) &&
        offer?.code === bookingRoom?.value?.publicOfferId,
    );

    if (!memberEnforced && !userStore.isLogged) return publicOffer;

    const memberOffer = availableRoom.value?.availabilityRoom?.offers?.find(
      offer =>
        offer?.categories?.some(isPromotionalOffer) &&
        (offer?.code === bookingRoom?.value?.memberOfferId ||
          offer?.code?.startsWith(
            publicOffer?.code ? `${publicOffer.code}-` : '',
          )),
    );

    return memberOffer || publicOffer;
  });

  // Room enhancements
  const enhancementsQueryVariables = computed<any>(() => ({
    hotelCode: hotelId.value,
    rooms: [
      {
        adults: bookingRoom?.value?.adults,
        childrenAges: bookingRoom?.value?.childrenAges,
        offerCode:
          userStore.isLogged && bookingRoom?.value?.memberOfferId
            ? bookingRoom?.value?.memberOfferId
            : bookingRoom?.value?.publicOfferId,
        period: {
          dateIn: bookingStore.dates.dateIn,
          nights: bookingStore.dates.nights,
        },
        productCode: bookingRoom?.value?.productCode,
      },
    ],
  }));
  const { result: enhancementsData } = useAvailableRoomEnhancementOptionsQuery(
    enhancementsQueryVariables,
  );
  const allEnhancements = computed(() =>
    enhancementsData.value?.availableRoomEnhancementOptions?.flatMap?.(
      enhancement => enhancement?.option,
    ),
  );

  const options = computed(() => {
    if (!(bookingRoom?.value?.options instanceof Map)) return [];

    return Array.from(
      bookingRoom?.value?.options.entries(),
    ).map<BookingRoomOptionEnhancement>(([_id, option]) => {
      const enhancement = allEnhancements.value?.find(
        enh => enh?.code === option.code,
      );

      return {
        bookingOption: option,
        apiOption: enhancement,
      } as BookingRoomOptionEnhancement;
    });
  });

  return { accommodation, availability, offer, options };
}
